import { FieldErrors } from "react-hook-form";
import { ValidationError } from "../components/ValidationErrorSidebar";

// Tab configuration for validation fields
export const TAB_CONFIG = {
  contract: {
    fields: [
      "fleetNumber",
      "insurance.contractStartDate", // Updated from leasingStartDate
      "insurance.startDate", // Updated path
      "leasing.leasingContractNumber",
      "leasing.replacedContractNumber",
    ],
  },
  client: {
    fields: [
      "client.roles",
      "client.isCompany",
      "client.code",
      "client.name",
      "client.firstName",
      "client.lastName",
      "client.birthNumber",
      "client.birthDate",
      "client.title",
      "client.titleType",
      "client.countryOfCitizenship",
      "client.age",
      "client.experience",
      "client.vatPayer",
      "client.electronicCommunication",
      "client.marketingConsent",
      "client.address.country",
      "client.address.city",
      "client.address.index",
      "client.address.street",
      "client.address.house",
      "client.address.flat",
      "client.address.suburb",
      "client.address.email",
      "client.address.mobilePhone",
      "client.address.phone",
      "client.correspondenceAddress.country",
      "client.correspondenceAddress.city",
      "client.correspondenceAddress.index",
      "client.correspondenceAddress.street",
      "client.correspondenceAddress.house",
      "client.correspondenceAddress.flat",
      "client.correspondenceAddress.suburb",
      "client.correspondenceAddress.email",
      "client.correspondenceAddress.mobilePhone",
      "client.correspondenceAddress.phone",
      "client.contactPerson.order",
      "client.contactPerson.firstName",
      "client.contactPerson.lastName",
      "client.contactPerson.function",
    ],
  },
  vehicle: {
    fields: [
      "vehicle.vin",
      "vehicle.licensePlate",
      "vehicle.registrationCertificateNumber",
      "vehicle.brand",
      "vehicle.model",
      "vehicle.fuel",
      "vehicle.vehicleCategory",
      "vehicle.usageType",
      "vehicle.registrationType",
      "vehicle.countryCode",
      "vehicle.manufactureDate",
      "vehicle.firstRegistrationDate",
      "vehicle.powerKw",
      "vehicle.cubicCapacityCcm",
      "vehicle.weight",
      "vehicle.seats",
      "vehicle.mileage",
      "vehicle.value",
      "vehicle.damagedBefore",
      "vehicle.repaired",
      "vehicle.registeredAbroad",
      "vehicle.twoYearRegistration",
      "vehicle.leaseType",
      "vehicle.financingShare",
      "vehicle.leaseFixedRate",
      "vehicle.owner.code",
      "vehicle.owner.name",
      "vehicle.owner.lastName",
      "vehicle.owner.firstName",
      "vehicle.owner.birthDate",
      "vehicle.owner.company",
      "kooperativaFields.damageSpecification",
      "kooperativaFields.minorDamageSpecification",
      "kooperativaFields.standingPlaces",
      "kooperativaFields.numberOfBeds",
      "kooperativaFields.notOperational",
      "kooperativaFields.technicalCertificateNumber",
    ],
  },
  coverages: {
    fields: [
      "selectedProducts",
      "selectedInsurers",
      "insurance.endDate",
      "insurance.payments",
      "insurance.coverageTerritory",
      "insurance.currency",
      "insurance.driverBirthDate",
      "insurance.driverAge",
      "insurance.driverLicenceDate",
      "insurance.driverExperience",
      "kooperativaFields.preferredLiquidation",
      "kooperativaFields.specialAgreement",
      "kooperativaFields.gainers",
      "kooperativaFields.risks",
    ],
  },
};

// Add this helper function to generate field label translation keys
const getFieldLabelKey = (fieldPath: string): string => {
  // Remove array indices for translation
  const cleanPath = fieldPath.replace(/\[\d+\]/g, "");

  // Special cases for nested fields
  if (cleanPath.startsWith("insurance.")) {
    const field = cleanPath.substring(10); // Remove 'insurance.'
    return `ila.insurance.${field}`;
  }
  if (cleanPath.startsWith("vehicle.")) {
    const field = cleanPath.substring(8); // Remove 'vehicle.'
    return `ila.vehicle.${field}`;
  }
  if (cleanPath.startsWith("clients.")) {
    const field = cleanPath.substring(8); // Remove 'clients.'
    if (field.includes("address.")) {
      const addressField = field.split("address.")[1];
      return `ila.client.address.${addressField}`;
    }
    if (field.includes("correspondenceAddress.")) {
      const addressField = field.split("correspondenceAddress.")[1];
      return `ila.client.correspondenceAddress.${addressField}`;
    }
    return `ila.client.${field}`;
  }
  if (cleanPath.startsWith("leasing.")) {
    const field = cleanPath.substring(8); // Remove 'leasing.'
    return `ila.leasing.${field}`;
  }
  if (cleanPath.startsWith("kooperativaFields.")) {
    const field = cleanPath.substring(18); // Remove 'kooperativaFields.'
    return `ila.kooperativa.${field}`;
  }

  // Default case
  return `ila.${cleanPath}`;
};

export function extractValidationErrors(
  errors: any, // Can be either nested or flattened
  t: (key: string, fallback?: string) => string
): ValidationError[] {
  const validationErrors: ValidationError[] = [];

  // Determine which tab an error belongs to
  const getTabInfo = (
    fieldPath: string
  ): { tabKey: string; tabIndex: number } => {
    const tabEntries = Object.entries(TAB_CONFIG);

    for (let i = 0; i < tabEntries.length; i++) {
      const [tabKey, config] = tabEntries[i];
      // Check if this field belongs to this tab
      if (
        config.fields.some((field) => {
          // Handle array fields like client.firstName
          const normalizedField = field.replace(/\[\d+\]/g, "[0]");
          const normalizedPath = fieldPath.replace(/\[\d+\]/g, "[0]");
          return (
            normalizedPath === normalizedField ||
            normalizedPath.startsWith(normalizedField + ".")
          );
        })
      ) {
        return { tabKey, tabIndex: i };
      }
    }

    // Default to contract tab if not found
    return { tabKey: "contract", tabIndex: 0 };
  };

  // Process flattened errors (our case)
  if (errors && typeof errors === "object") {
    Object.entries(errors).forEach(([fieldPath, errorValue]) => {
      if (errorValue) {
        const { tabKey, tabIndex } = getTabInfo(fieldPath);

        // The error message is already translated/localized
        const message =
          typeof errorValue === "string"
            ? errorValue
            : (errorValue as any).message || String(errorValue);

        // Get translated field label
        const fieldLabelKey = getFieldLabelKey(fieldPath);
        const fieldLabel = t(fieldLabelKey, fieldPath); // Fallback to fieldPath if translation not found

        // Get translated tab name
        const tabLabel = t(`ila.${tabKey}Section.label`, tabKey);

        validationErrors.push({
          field: fieldLabel, // Translated field name
          fieldPath: fieldPath, // Keep original for DOM queries
          message: message, // Already localized error message
          tab: tabLabel, // Translated tab name
          tabIndex,
        });
      }
    });
  }

  return validationErrors;
}
